import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { authService } from '../api'
import { User } from '../api/types'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  isEmbeddedPage: boolean
  patientID: string | null
}

const initialState: AuthState = {
  user: authService.getCurrentUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
  isEmbeddedPage: false,
  patientID: null,
}

// Async thunks
export const sendOTP = createAsyncThunk(
  'auth/sendOTP',
  async (mobile: string, { rejectWithValue }) => {
    try {
      const response = await authService.sendOTP(mobile)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to send OTP')
    }
  }
)

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ mobile, otp }: { mobile: string; otp: string }, { rejectWithValue }) => {
    try {
      const result = await authService.verifyOTP(mobile, otp)
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to verify OTP')
    }
  }
)

export const signUp = createAsyncThunk(
  'auth/signUp',
  async ({ userData, otp }: { userData: any; otp: string }, { rejectWithValue }) => {
    try {
      const user = await authService.signUp(userData, otp)
      return user
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to sign up')
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: any, { rejectWithValue }) => {
    try {
      const user = await authService.updateProfile(profileData)
      return user
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile')
    }
  }
)

export const signOut = createAsyncThunk('auth/signOut', async () => {
  authService.signOut()
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload
      state.isAuthenticated = !!action.payload
    },
    setEmbeddedPage: (state, action: PayloadAction<boolean>) => {
      state.isEmbeddedPage = action.payload
    },
    setPatientID: (state, action: PayloadAction<string | null>) => {
      state.patientID = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Send OTP
      .addCase(sendOTP.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false
        if (!action.payload.isNewUser) {
          state.user = action.payload.user
          state.isAuthenticated = true
        }
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Sign Up
      .addCase(signUp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Sign Out
      .addCase(signOut.fulfilled, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
      })
  },
})

export const { clearError, setUser, setEmbeddedPage, setPatientID  } = authSlice.actions
export default authSlice.reducer 